import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { defaultFlags, type FeatureFlagKey, type FlagsSnapshot } from '@fyso/shared/feature-flags';
import { api, type FlagStatusPayload } from '@/lib/api';
import { isAgentsEmbed } from '@/lib/agents-embed';
import { useAuth } from '@/contexts/auth-context';

/**
 * Per-user feature flags, resolved by the API (default → deploy → this user's
 * override). The UI only reads the result; it never decides a flag's value.
 */
export interface FlagsContextValue {
  flags: FlagsSnapshot;
  /** Labelled list for the Experiments screen. */
  items: FlagStatusPayload[];
  /** Whether this session may change flags. */
  editable: boolean;
  loading: boolean;
  /** Persist one flag. Resolves only after the API stored it; rejects otherwise. */
  setFlag: (key: FeatureFlagKey, value: boolean) => Promise<void>;
}

/** Outside a provider (embeds, tests, first paint) every flag has its declared default. */
const fallback: FlagsContextValue = {
  flags: defaultFlags(),
  items: [],
  editable: false,
  loading: false,
  setFlag: async () => { throw new Error('Flags are unavailable in this session'); },
};

export const FlagsContext = createContext<FlagsContextValue>(fallback);

/** Test/preview helper: a context value with the given flags switched on or off. */
export function flagsValue(overrides: Partial<Record<FeatureFlagKey, boolean>> = {}): FlagsContextValue {
  return { ...fallback, flags: { ...defaultFlags(), ...overrides } };
}

export function FlagsProvider({ children }: { children: ReactNode }) {
  const { admin, isAuthenticated, isTenantUser } = useAuth();
  // The Business embed has its own transport and no Experiments: it always runs on defaults.
  const userId = isAuthenticated && !isTenantUser && !isAgentsEmbed() ? admin?.id ?? null : null;
  const [state, setState] = useState<{ userId: string | null; flags: FlagsSnapshot; items: FlagStatusPayload[]; editable: boolean }>(
    { userId: null, flags: fallback.flags, items: [], editable: false },
  );

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    api.flags.get()
      .then((data) => { if (!cancelled) setState({ userId, flags: { ...defaultFlags(), ...data.flags }, items: data.items, editable: data.editable }); })
      // Not reading flags must never block the app: keep the defaults.
      .catch(() => { if (!cancelled) setState({ userId, flags: fallback.flags, items: [], editable: false }); });
    return () => { cancelled = true; };
  }, [userId]);

  // Never show the previous user's flags after a logout or an account switch.
  const current = state.userId === userId && userId ? state : null;

  const setFlag = useCallback(async (key: FeatureFlagKey, value: boolean) => {
    const data = await api.flags.set(key, value);
    setState({ userId, flags: { ...defaultFlags(), ...data.flags }, items: data.items, editable: data.editable });
  }, [userId]);

  const value = useMemo<FlagsContextValue>(() => ({
    flags: current?.flags ?? fallback.flags,
    items: current?.items ?? [],
    editable: current?.editable ?? false,
    loading: !!userId && !current,
    setFlag,
  }), [current, userId, setFlag]);

  return <FlagsContext.Provider value={value}>{children}</FlagsContext.Provider>;
}

export function useFlags() {
  return useContext(FlagsContext);
}

/** Whether `key` is on for the signed-in user. */
export function useFlag(key: FeatureFlagKey): boolean {
  return useContext(FlagsContext).flags[key];
}
